<template>
  <div>
    <!-- Encabezado y filtros -->
    <b-row class="align-items-center mb-1 no-print">
      <b-col>
        <h1 class="font-weight-bolder mb-0">
          {{ $t('suppliesExplosionYoc') }}
        </h1>
        <p class="text-muted mb-0 small">
          {{ $t('suppliesExplosionSubtitle') }}
        </p>
      </b-col>

      <b-col
        cols="auto"
        class="no-print"
      >
        <div class="d-flex align-items-center flex-wrap justify-content-end">
          <b-input-group
            size="sm"
            class="mr-1 mb-1"
            style="width: 260px;"
          >
            <b-form-input
              v-model.trim="inputSearch"
              :placeholder="$t('searchSupplyPlaceholder')"
              :disabled="suppliesExplosionLoading"
              @keyup.enter="applyFilters"
            />
            <b-input-group-append>
              <b-button
                variant="primary"
                :disabled="suppliesExplosionLoading"
                @click="applyFilters"
              >
                {{ $t('search') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <b-input-group
            size="sm"
            class="mr-1 mb-1"
            style="width: 230px;"
          >
            <b-input-group-prepend is-text>
              {{ $t('supplier') }}
            </b-input-group-prepend>
            <b-form-select
              v-model="inputSupplierId"
              :options="supplierSelectOptions"
              :disabled="suppliesExplosionLoading"
              @change="applyFilters"
            />
          </b-input-group>

          <b-input-group
            size="sm"
            class="mr-1 mb-1"
            style="width: 190px;"
          >
            <b-input-group-prepend is-text>
              {{ $t('month') }}
            </b-input-group-prepend>
            <b-form-input
              v-model="inputMonth"
              type="month"
              :disabled="suppliesExplosionLoading"
              @change="applyFilters"
            />
          </b-input-group>

          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 mb-1"
            :disabled="suppliesExplosionLoading || !hasActiveFilters"
            @click="clearFilters"
          >
            {{ $t('clearFilters') }}
          </b-button>

          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 mb-1"
            :disabled="suppliesExplosionLoading || groups.length === 0"
            @click="exportExcel"
          >
            {{ $t('exportExcel') }}
          </b-button>

          <b-button
            variant="outline-secondary"
            size="sm"
            class="mb-1"
            @click="printPage"
          >
            {{ $t('print') }}
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Totales -->
    <b-row class="mb-1">
      <b-col>
        <div class="totals-surface p-1">
          <div class="d-flex align-items-center justify-content-between flex-wrap mb-1">
            <span class="totals-title">{{ monthLabel }}</span>
            <div>
              <span class="totals-pill mr-1">{{ $t('supplies') }}: {{ formatNumber(totals.products_count) }}</span>
              <span class="totals-pill mr-1">{{ $t('providers') }}: {{ formatNumber(totals.suppliers_count) }}</span>
              <span class="totals-pill">{{ $t('quotes') }}: {{ formatNumber(totals.quotes_count) }}</span>
            </div>
          </div>
          <b-row>
            <b-col
              md="4"
              class="mb-1 mb-md-0"
            >
              <div class="metric-card metric-primary">
                <div class="metric-label">
                  {{ $t('purchaseTotalToSuppliers') }}
                </div>
                <div class="metric-value">
                  {{ formatCurrency(totals.purchase_total) }}
                </div>
                <div class="metric-sub">
                  {{ $t('withoutVat') }}
                </div>
              </div>
            </b-col>
            <b-col
              md="4"
              class="mb-1 mb-md-0"
            >
              <div class="metric-card">
                <div class="metric-label">
                  {{ $t('saleTotalToCustomers') }}
                </div>
                <div class="metric-value">
                  {{ formatCurrency(totals.sale_total) }}
                </div>
                <div class="metric-sub">
                  {{ $t('withoutVat') }}
                </div>
              </div>
            </b-col>
            <b-col md="4">
              <div class="metric-card">
                <div class="metric-label">
                  {{ hasCreditSurcharge ? $t('netCommission') : $t('itemsCommission') }}
                </div>
                <div class="metric-value">
                  {{ formatCurrency(totals.commission_total) }}
                </div>
                <div class="commission-breakdown">
                  <div class="breakdown-line">
                    <span>{{ $t('itemsCommission') }}</span>
                    <span>{{ formatCurrency(totals.items_commission) }}</span>
                  </div>
                  <div
                    v-if="hasCreditSurcharge"
                    class="breakdown-line"
                  >
                    <span>+ {{ $t('creditSurchargeCommission') }}</span>
                    <span>{{ formatCurrency(totals.credit_surcharge_commission) }}</span>
                  </div>
                </div>
                <div class="metric-sub">
                  {{ formatPercentage(totals.commission_pct) }} · {{ $t('overNetSale') }}
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>

    <!-- Selector de agrupación y expansión -->
    <b-row class="mb-1 no-print">
      <b-col class="d-flex align-items-center justify-content-between flex-wrap">
        <b-form-radio-group
          :checked="suppliesExplosionGroupBy"
          buttons
          button-variant="outline-primary"
          size="sm"
          :disabled="suppliesExplosionLoading"
          @change="changeGroupBy"
        >
          <b-form-radio value="product">
            {{ $t('groupBySupply') }}
          </b-form-radio>
          <b-form-radio value="supplier">
            {{ $t('groupByProvider') }}
          </b-form-radio>
        </b-form-radio-group>

        <div>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            :disabled="suppliesExplosionLoading || allExpanded || groups.length === 0"
            @click="expandAll"
          >
            <feather-icon
              icon="ChevronsDownIcon"
              class="mr-25"
            />
            {{ $t('expandAll') }}
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="suppliesExplosionLoading || expandedKeys.length === 0"
            @click="collapseAll"
          >
            <feather-icon
              icon="ChevronsUpIcon"
              class="mr-25"
            />
            {{ $t('collapseAll') }}
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Tabla -->
    <div class="table-responsive">
      <b-table
        striped
        hover
        responsive
        show-empty
        :busy="suppliesExplosionLoading"
        :items="rows"
        :fields="fields"
        :empty-text="$t('noData')"
        class="supplies-explosion-table"
        @row-clicked="toggleRow"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('loading') }}</strong>
          </div>
        </template>

        <template #cell(index)="data">
          {{ formatNumber(data.index + 1) }}
        </template>

        <template #cell(name)="data">
          <div class="font-weight-bolder">
            {{ data.item.name || $t('unassignedSupplier') }}
          </div>
          <small class="text-muted">{{ subtitleFor(data.item) }}</small>
        </template>

        <template #cell(unit_type)="data">
          {{ data.item.unit_type ? $t(unitTypeKey(data.item.unit_type)) : '—' }}
        </template>

        <template #cell(quantity)="data">
          {{ formatNumber(data.item.quantity) }}
        </template>

        <template #cell(purchase_amount)="data">
          {{ formatCurrency(data.item.purchase_amount) }}
        </template>

        <template #cell(sale_amount)="data">
          {{ formatCurrency(data.item.sale_amount) }}
        </template>

        <template #cell(commission)="data">
          {{ formatCurrency(data.item.commission) }}
        </template>

        <template #cell(avg_unit_price)="data">
          {{ formatCurrency(data.item.avg_unit_price) }}
        </template>

        <template #cell(last_purchase_at)="data">
          {{ formatDate(data.item.last_purchase_at) }}
        </template>

        <template #cell(details)="data">
          <b-button
            size="sm"
            variant="flat-primary"
            class="btn-icon"
            @click.stop="toggleRow(data.item)"
          >
            <feather-icon :icon="isExpanded(data.item) ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
          </b-button>
        </template>

        <template #row-details="data">
          <b-card
            no-body
            class="child-card mb-0"
          >
            <b-table
              small
              borderless
              :items="data.item.children"
              :fields="childFields"
              class="mb-0"
            >
              <template #cell(name)="child">
                <div>{{ child.item.name || $t('unassignedSupplier') }}</div>
                <small class="text-muted">{{ childSubtitleFor(child.item) }}</small>
              </template>

              <template #cell(unit_type)="child">
                {{ child.item.unit_type ? $t(unitTypeKey(child.item.unit_type)) : '—' }}
              </template>

              <template #cell(quantity)="child">
                {{ formatNumber(child.item.quantity) }}
              </template>

              <template #cell(purchase_amount)="child">
                {{ formatCurrency(child.item.purchase_amount) }}
              </template>

              <template #cell(avg_unit_price)="child">
                {{ formatCurrency(child.item.avg_unit_price) }}
              </template>

              <template #cell(share)="child">
                <div class="d-flex align-items-center">
                  <b-progress
                    :value="child.item.share * 100"
                    max="100"
                    height="6px"
                    class="flex-grow-1 mr-1 share-bar"
                  />
                  <small class="text-muted share-label">{{ formatPercentage(child.item.share) }}</small>
                </div>
              </template>

              <template #cell(last_purchase_at)="child">
                {{ formatDate(child.item.last_purchase_at) }}
              </template>
            </b-table>
          </b-card>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'SuppliesExplosion',

  data() {
    return {
      inputMonth: this.$route.query.month || '',
      inputSearch: this.$route.query.search || '',
      inputSupplierId: this.$route.query.supplier_id || null,
      expandedKeys: [],
    }
  },

  computed: {
    ...mapGetters('suppliesExplosion', [
      'suppliesExplosionGroups',
      'suppliesExplosionTotals',
      'suppliesExplosionGroupBy',
      'suppliesExplosionSupplierOptions',
      'suppliesExplosionLoading',
    ]),

    groups() {
      return this.suppliesExplosionGroups
    },

    totals() {
      return this.suppliesExplosionTotals
    },

    groupedByProduct() {
      return this.suppliesExplosionGroupBy !== 'supplier'
    },

    rows() {
      return this.groups.map(group => ({
        ...group,
        _showDetails: this.expandedKeys.includes(group.key),
      }))
    },

    supplierSelectOptions() {
      return [
        { value: null, text: this.$t('all') },
        ...this.suppliesExplosionSupplierOptions,
      ]
    },

    fields() {
      if (this.groupedByProduct) {
        return [
          { key: 'index', label: 'No.' },
          { key: 'name', label: this.$t('supply'), sortable: true },
          { key: 'category', label: this.$t('category'), sortable: true },
          { key: 'unit_type', label: this.$t('unit') },
          { key: 'quantity', label: this.$t('quantity'), sortable: true },
          { key: 'purchase_amount', label: this.$t('purchaseAmount'), sortable: true },
          { key: 'avg_unit_price', label: this.$t('puAverage'), sortable: true },
          { key: 'suppliers_count', label: this.$t('providers'), sortable: true },
          { key: 'quotes_count', label: this.$t('quotes'), sortable: true },
          { key: 'last_purchase_at', label: this.$t('lastPurchase'), sortable: true },
          { key: 'details', label: '' },
        ]
      }

      return [
        { key: 'index', label: 'No.' },
        { key: 'name', label: this.$t('supplier'), sortable: true },
        { key: 'products_count', label: this.$t('supplies'), sortable: true },
        { key: 'purchase_amount', label: this.$t('purchaseAmount'), sortable: true },
        { key: 'sale_amount', label: this.$t('saleAmount'), sortable: true },
        { key: 'commission', label: this.$t('commission'), sortable: true },
        { key: 'quotes_count', label: this.$t('quotes'), sortable: true },
        { key: 'last_purchase_at', label: this.$t('lastPurchase'), sortable: true },
        { key: 'details', label: '' },
      ]
    },

    childFields() {
      if (this.groupedByProduct) {
        return [
          { key: 'name', label: this.$t('supplier') },
          { key: 'quantity', label: this.$t('quantity') },
          { key: 'purchase_amount', label: this.$t('purchaseAmount') },
          { key: 'avg_unit_price', label: this.$t('puAverage') },
          { key: 'share', label: this.$t('shareOfSupply') },
          { key: 'quotes_count', label: this.$t('quotes') },
          { key: 'last_purchase_at', label: this.$t('lastPurchase') },
        ]
      }

      return [
        { key: 'name', label: this.$t('supply') },
        { key: 'unit_type', label: this.$t('unit') },
        { key: 'quantity', label: this.$t('quantity') },
        { key: 'purchase_amount', label: this.$t('purchaseAmount') },
        { key: 'avg_unit_price', label: this.$t('puAverage') },
        { key: 'share', label: this.$t('shareOfProvider') },
        { key: 'quotes_count', label: this.$t('quotes') },
      ]
    },

    hasActiveFilters() {
      return !!this.inputSearch || !!this.inputSupplierId
    },

    hasCreditSurcharge() {
      const surcharge = this.totals.credit_surcharge_commission
      return surcharge !== null && surcharge !== undefined
    },

    allExpanded() {
      return this.groups.length > 0 && this.expandedKeys.length === this.groups.length
    },

    currentMonth() {
      const date = new Date()
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    },

    monthLabel() {
      const month = this.inputMonth || this.currentMonth
      const [year, monthNumber] = month.split('-')
      const names = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
      return `${names[Number(monthNumber) - 1] || monthNumber} ${year}`
    },
  },

  created() {
    let { month } = this.$route.query
    if (!month) {
      month = this.currentMonth
      this.$router.replace({ query: { ...this.$route.query, month } }).catch(() => {})
    }
    this.inputMonth = month

    this.setSuppliesExplosionFilter({ field: 'month', value: month })
    this.setSuppliesExplosionFilter({ field: 'search', value: this.inputSearch })
    this.setSuppliesExplosionFilter({ field: 'supplier_id', value: this.inputSupplierId })
    this.fetchSuppliesExplosion()
  },

  methods: {
    ...mapActions('suppliesExplosion', [
      'fetchSuppliesExplosion',
      'setSuppliesExplosionFilter',
      'setSuppliesExplosionGroupBy',
      'clearSuppliesExplosionFilters',
    ]),

    applyFilters() {
      const month = this.inputMonth || this.currentMonth
      this.inputMonth = month
      this.expandedKeys = []

      this.setSuppliesExplosionFilter({ field: 'month', value: month })
      this.setSuppliesExplosionFilter({ field: 'search', value: this.inputSearch })
      this.setSuppliesExplosionFilter({ field: 'supplier_id', value: this.inputSupplierId })

      const query = { month }
      if (this.inputSearch) query.search = this.inputSearch
      if (this.inputSupplierId) query.supplier_id = this.inputSupplierId
      this.$router.replace({ query }).catch(() => {})

      this.fetchSuppliesExplosion()
    },

    clearFilters() {
      this.inputSearch = ''
      this.inputSupplierId = null
      this.expandedKeys = []
      this.$router.replace({ query: { month: this.inputMonth || this.currentMonth } }).catch(() => {})
      this.clearSuppliesExplosionFilters()
    },

    changeGroupBy(value) {
      this.expandedKeys = []
      this.setSuppliesExplosionGroupBy(value)
    },

    isExpanded(item) {
      return this.expandedKeys.includes(item.key)
    },

    toggleRow(item) {
      this.expandedKeys = this.isExpanded(item)
        ? this.expandedKeys.filter(key => key !== item.key)
        : [...this.expandedKeys, item.key]
    },

    expandAll() {
      this.expandedKeys = this.groups.map(group => group.key)
    },

    collapseAll() {
      this.expandedKeys = []
    },

    subtitleFor(item) {
      if (this.groupedByProduct) {
        return [item.sku, item.brand].filter(Boolean).join(' · ')
      }
      return (item.branches || []).join(' · ')
    },

    childSubtitleFor(child) {
      if (this.groupedByProduct) {
        return child.branch_name || ''
      }
      return [child.sku, child.brand].filter(Boolean).join(' · ')
    },

    unitTypeKey(unitType) {
      const keys = {
        meter: 'meter',
        square_meters: 'squareMeters',
        cubic_meters: 'cubicMeters',
        piece: 'piece',
        kilogram: 'kilogram',
        ton: 'ton',
        bundle: 'bundle',
        box: 'box',
        trip: 'trip',
        gram: 'gram',
        unit: 'unit',
        lot: 'lot',
        equipment: 'equipment',
        service: 'service',
      }
      return keys[unitType] || unitType
    },

    formatCurrency(value) {
      const num = Number(value)
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(Number.isNaN(num) ? 0 : num)
    },

    formatNumber(value) {
      const num = Number(value)
      return new Intl.NumberFormat('es-MX').format(Number.isNaN(num) ? 0 : num)
    },

    formatPercentage(value) {
      const num = Number(value)
      return `${(Number.isNaN(num) ? 0 : num * 100).toFixed(2)}%`
    },

    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      return `${day}-${month}-${date.getFullYear()}`
    },

    printPage() {
      window.print()
    },

    exportRows() {
      const rows = []

      this.groups.forEach((group, index) => {
        if (this.groupedByProduct) {
          rows.push([
            index + 1,
            group.name,
            group.sku || '',
            group.brand || '',
            group.category || '',
            group.unit_type ? this.$t(this.unitTypeKey(group.unit_type)) : '',
            group.quantity,
            group.purchase_amount,
            group.avg_unit_price,
            group.suppliers_count,
            group.quotes_count,
            this.formatDate(group.last_purchase_at),
            '',
          ])
          group.children.forEach(child => {
            rows.push([
              '',
              `→ ${child.name || this.$t('unassignedSupplier')}`,
              child.branch_name || '',
              '',
              '',
              '',
              child.quantity,
              child.purchase_amount,
              child.avg_unit_price,
              '',
              child.quotes_count,
              this.formatDate(child.last_purchase_at),
              this.formatPercentage(child.share),
            ])
          })
        } else {
          rows.push([
            index + 1,
            group.name || this.$t('unassignedSupplier'),
            (group.branches || []).join(' · '),
            group.products_count,
            group.purchase_amount,
            group.sale_amount,
            group.commission,
            group.quotes_count,
            this.formatDate(group.last_purchase_at),
            '',
          ])
          group.children.forEach(child => {
            rows.push([
              '',
              `→ ${child.name}`,
              child.unit_type ? this.$t(this.unitTypeKey(child.unit_type)) : '',
              child.quantity,
              child.purchase_amount,
              '',
              '',
              child.quotes_count,
              '',
              this.formatPercentage(child.share),
            ])
          })
        }
      })

      return rows
    },

    exportHeaders() {
      if (this.groupedByProduct) {
        return [
          'No.',
          this.$t('supply'),
          'SKU',
          this.$t('brand'),
          this.$t('category'),
          this.$t('unit'),
          this.$t('quantity'),
          this.$t('purchaseAmount'),
          this.$t('puAverage'),
          this.$t('providers'),
          this.$t('quotes'),
          this.$t('lastPurchase'),
          this.$t('shareOfSupply'),
        ]
      }

      return [
        'No.',
        this.$t('supplier'),
        this.$t('branches'),
        this.$t('supplies'),
        this.$t('purchaseAmount'),
        this.$t('saleAmount'),
        this.$t('commission'),
        this.$t('quotes'),
        this.$t('lastPurchase'),
        this.$t('shareOfProvider'),
      ]
    },

    exportExcel() {
      const headers = this.exportHeaders()
      const body = this.exportRows()

      const escape = value => String(value === null || value === undefined ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      const tableHtml = `
        <table border="1">
          <thead>
            <tr>${headers.map(header => `<th>${escape(header)}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${body.map(row => `<tr>${row.map(cell => `<td>${escape(cell)}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>
      `

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8">
            <style>
              body { font-family: Arial, Helvetica, sans-serif; color: #3b3f5c; margin: 24px; }
              h1 { margin: 0 0 4px 0; font-size: 22px; }
              table { border-collapse: collapse; }
              th { background: #f8f9fa; font-size: 12px; text-align: left; padding: 6px 10px; }
              td { font-size: 12px; padding: 4px 10px; }
            </style>
          </head>
          <body>
            <h1>${escape(this.$t('suppliesExplosionYoc'))}</h1>
            <p>${escape(this.monthLabel)} · ${escape(this.$t('purchaseTotalToSuppliers'))}: ${escape(this.formatCurrency(this.totals.purchase_total))}</p>
            ${tableHtml}
          </body>
        </html>
      `

      const safeMonth = (this.inputMonth || this.currentMonth).replace(/[^0-9-]/g, '')
      const blob = new Blob(['﻿', html], { type: 'application/vnd.ms-excel;charset=utf-8' })
      saveAs(blob, `explosion-insumos-yoc_${this.groupedByProduct ? 'insumo' : 'proveedor'}_${safeMonth}.xls`)
    },
  },
}
</script>

<style scoped>
.totals-surface {
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
  border-radius: 14px;
}
.totals-title {
  font-size: 1rem;
  font-weight: 800;
}
.totals-pill {
  background: rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-weight: 700;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
}
.metric-card {
  height: 100%;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.metric-primary {
  border-color: rgba(0, 0, 0, 0.1);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
}
.metric-label {
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.metric-value {
  margin-top: 4px;
  font-weight: 900;
  font-size: 1.35rem;
}
.metric-sub {
  margin-top: 6px;
  color: rgba(0, 0, 0, 0.48);
  font-size: 0.78rem;
}
.commission-breakdown {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
}
.breakdown-line {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.78rem;
}
.child-card {
  background: rgba(0, 0, 0, 0.02);
  border-radius: 10px;
  padding: 4px 8px;
}
.share-bar {
  min-width: 60px;
}
.share-label {
  white-space: nowrap;
}
.supplies-explosion-table >>> tbody tr {
  cursor: pointer;
}
@media print {
  .no-print { display: none !important; }
}
</style>
